import React from 'react';
import Card from '@jbhi-fi/lanyard-ui/components/Card';
import { styles } from './SpecialOffer.css';
import { classNames } from '@jbhi-fi/lanyard-ui/global/utils';
import { SpecialOffer as SpecialOfferType } from '../PdpProductProvider/types';
import Button from '@jbhi-fi/lanyard-ui/components/Button/Button';
import ArrowIcon from '@jbhi-fi/lanyard-ui/components/Icons/Arrow';
import ChatIcon from '@jbhi-fi/lanyard-ui/components/Icons/Chat';
import { palette } from '@jbhi-fi/lanyard-ui/themes';
import { useNavigate } from '../PlatformFunctionality';
import { useTrackingContext } from '../Tracking';
import { EX_DISPLAY_URL_HASH_ID } from '../ProductDetail/SpecialOffers/constants';

export type SpecialOfferProps = {
  offer: SpecialOfferType;
  className?: string;
  onGoToAccordion?: (id: string) => void;
};

export const SpecialOffer = ({ offer, className, onGoToAccordion }: SpecialOfferProps) => {
  const { title, content, imageUrl, disclaimer, chat, links, endDate } = offer;
  const hasLinks = (links?.length ?? 0) > 0;
  const hasOtherContent = !!(title || content || hasLinks || disclaimer || chat);
  const navigate = useNavigate();
  const { tracker } = useTrackingContext();

  const handleChatClick = () => {
    window.openChatButtonClick();

    tracker.trackCustomEvent({
      event: 'pdp-specialoffer-chat',
      action: 'Chat Button Click',
      pageType: window.gtm.pageType,
      ecommerce: {
        products: [window.gtm.product],
      },
    });
  };

  return (
    <div className={classNames([className, styles.container])}>
      <Card shadow={2} spacing={0} backgroundColor={palette.secondary.main}>
        {imageUrl ? (
          <div className={hasOtherContent ? undefined : styles.imageOnlyContainer}>
            <img src={imageUrl} className={styles.image} alt={title} />
          </div>
        ) : null}

        {hasOtherContent ? (
          <div className={styles.contentSection}>
            {title ? (
              <div className={styles.title}>
                <span dangerouslySetInnerHTML={{ __html: title }} />
              </div>
            ) : null}

            <div className={styles.content}>
              {content ? <div dangerouslySetInnerHTML={{ __html: content }} /> : null}
              {chat ? (
                <div className={styles.chatButtonContainer}>
                  <Button
                    type="linkWithIcon"
                    iconPosition="left"
                    label={chat.label}
                    onClick={() => handleChatClick()}
                    customStyles={{ textAlign: 'left' }}
                    icon={<ChatIcon className={styles.chatIcon} />}
                  />
                </div>
              ) : null}

              {hasLinks && onGoToAccordion ? (
                <div className={styles.links}>
                  {links?.map(link => {
                    return (
                      <div key={link.label}>
                        <Button
                          type="linkWithIcon"
                          label={link.label}
                          onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            onGoToAccordion(EX_DISPLAY_URL_HASH_ID);
                          }}
                          customStyles={{ textAlign: 'left' }}
                          icon={<ArrowIcon className={styles.arrowIcon} />}
                        />
                      </div>
                    );
                  })}
                </div>
              ) : hasLinks ? (
                <div className={styles.links}>
                  {links?.map(link => {
                    return (
                      <div key={link.label}>
                        <Button
                          type="linkWithIcon"
                          label={link.label}
                          onClick={() => navigate(link.url)}
                          customStyles={{ textAlign: 'left' }}
                          icon={<ArrowIcon className={styles.arrowIcon} />}
                        />
                      </div>
                    );
                  })}
                </div>
              ) : null}
              {disclaimer || endDate ? (
                <div className={styles.disclaimer}>
                  {disclaimer ? (
                    <span className={styles.disclaimerContent} dangerouslySetInnerHTML={{ __html: disclaimer }} />
                  ) : null}
                  {endDate ? <span className={styles.disclaimerContent}> Offer ends {endDate}</span> : null}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </Card>
    </div>
  );
};
